import { css } from '@emotion/react'
import { Heading, HFlow, Text, VFlow } from 'bold-ui'
import { Divider } from 'components/divider/Divider'
import { HLabel } from 'components/HLabel'
import { useFlags } from 'config/useFlagsContext'
import { useProblemasModalSimplificadoQuery } from 'graphql/hooks.generated'
import { Problema, TipoAcompanhamentoEnum } from 'graphql/types.generated'
import { Fragment } from 'react'
import { emptyArrayOf } from 'util/array'
import { calculateIdadeGestacional } from 'util/atendimento/gestante/calculateIdadeGestacional'
import { isAfterOrEqual, isBeforeOrEqual } from 'util/date/compare'
import { formatDate, toDate } from 'util/date/formatDate'
import { humanizeDate } from 'util/date/humanize-date'
import { SituacaoProblemaTag } from 'view/atendimentos/detail/components/SituacaoProblemaTag'
import { formatIdadeGestacional } from 'view/atendimentos/detail/soap/pre-natal/util-preNatal'

import { AcompanhamentoAccordionCard } from '../../components/AcompanhamentoAccordionCard'
import { sortProblemas } from '../util-acompanhamentoPreNatal'

interface ListaProblemasCondicoesCardProps {
  prontuarioId: ID
  dataInicioGestacao: Date
  dataInicioPuerperio?: Date
  dataFimPuerperio?: Date
  tipoAcompanhamento: TipoAcompanhamentoEnum
}

export function ListaProblemasCondicoesCard(props: Readonly<ListaProblemasCondicoesCardProps>) {
  const { prontuarioId, dataInicioGestacao, dataInicioPuerperio, dataFimPuerperio, tipoAcompanhamento } = props

  const styles = createStyles()
  const isAcompanhamentoPuerperio = tipoAcompanhamento === TipoAcompanhamentoEnum.PUERPERIO
  const { PILOTO_CID11_ENABLED } = useFlags()

  const {
    loading,
    data: { problemas },
  } = useProblemasModalSimplificadoQuery({
    variables: {
      filtro: {
        prontuarioId,
        periodoInicioProblema: { endDate: isAcompanhamentoPuerperio ? dataFimPuerperio : null },
        includeInicioNull: true,
      },
    },
  })

  const sortedProblemas = (problemas?.content ?? emptyArrayOf<Problema>())?.toSorted((a: Problema, b: Problema) =>
    sortProblemas(a, b, tipoAcompanhamento)
  )
  const problemasToShow = sortedProblemas?.slice(0, 2)
  const problemasAccordion = sortedProblemas?.slice(2)

  const renderProblemaRow = (index: number, problema: Problema) => {
    const { ciap, cid10, cid11, ultimaEvolucao, situacao } = problema
    const humanizedStartDate = ultimaEvolucao.dataInicio && humanizeDate(ultimaEvolucao.dataInicio)
    const hasIgDeInicio =
      ultimaEvolucao.dataInicio &&
      isAfterOrEqual(toDate(ultimaEvolucao.dataInicio), dataInicioGestacao) &&
      isBeforeOrEqual(toDate(ultimaEvolucao.dataInicio), dataInicioPuerperio)
    const igDeInicio = hasIgDeInicio
      ? calculateIdadeGestacional(dataInicioGestacao, toDate(ultimaEvolucao.dataInicio))
      : null

    return (
      <Fragment key={problema.id ?? index}>
        {!!index && <Divider />}
        <HFlow
          justifyContent='space-between'
          alignItems='center'
          style={css`
            padding: 0.5rem;
          `}
        >
          <VFlow vSpacing={0.125}>
            <Heading level={4} style={styles.separator}>
              {ciap && (
                <Fragment>
                  {ciap.descricao} <Text fontWeight='initial'>(CIAP2 {ciap.codigo})</Text>
                </Fragment>
              )}
              {cid10 && (
                <Fragment>
                  {cid10.nome} <Text fontWeight='initial'>(CID10 {cid10.codigo})</Text>
                </Fragment>
              )}
              {PILOTO_CID11_ENABLED && cid11 && (
                <Fragment>
                  {cid11.nome.toUpperCase()} <Text fontWeight='initial'>(CID11 {cid11.codigo})</Text>
                </Fragment>
              )}
            </Heading>
            <HFlow hSpacing={0.5}>
              <HLabel title='Início:'>
                {ultimaEvolucao.dataInicio &&
                  `${formatDate(ultimaEvolucao.dataInicio)} | ${
                    humanizedStartDate === 'Hoje' ? humanizedStartDate : `Há ${humanizedStartDate}`
                  }`}
              </HLabel>
              <HLabel title='Última avaliação:'>
                {ultimaEvolucao.atualizadoEm && formatDate(ultimaEvolucao.atualizadoEm)}
              </HLabel>
            </HFlow>
            <HLabel title='IG de início:'>{formatIdadeGestacional(igDeInicio?.emDias)}</HLabel>
          </VFlow>
          <SituacaoProblemaTag situacao={situacao} />
        </HFlow>
      </Fragment>
    )
  }

  return (
    <AcompanhamentoAccordionCard
      id='lista-problemas-condicoes'
      title='Lista de problemas e condições'
      itemName='problema'
      tipoAcompanhamento={tipoAcompanhamento}
      loading={loading}
      items={problemasToShow}
      accordionItems={problemasAccordion}
      renderRow={(index, problema) => renderProblemaRow(index, problema as Problema)}
    />
  )
}

const createStyles = () => ({
  separator: css`
    & > :not(:last-child):after {
      content: '|';
      margin: 0 0.25rem;
    }
  `,
})
